import { Entity } from '@api/api/entity-schema';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useKgInsight } from '@terminal/react-query-hooks/insights/useKgInsight';
import { useKgInsightFilter } from '@terminal/react-query-hooks/insights/useKgInsightFilter';
import { queryKeys } from '@terminal/routes/query-params';
import { EarningsInsightWidget } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/EarningsInsightWidget';
import { FC, useEffect, useRef } from 'react';
import { EarningsKgInsightWidget } from './components/earnings-kg-insight-widget/EarningsKgInsightWidget';
import { KgInsightSkeleton } from './components/kg-insight-content/KgInsightSkeleton';

interface EarningsCatalystControllerProps {
  navigateToKGInsight: () => void;
  expandCitations: boolean;
  entity?: Entity;
}

export const EarningsCatalystController: FC<
  EarningsCatalystControllerProps
> = ({ navigateToKGInsight, expandCitations, entity }) => {
  const { params, setQueryParams } = useQueryParams();
  const insightId = params[queryKeys.insightId] ?? '';
  const earningsId = params[queryKeys.earningsId] ?? '';
  const activeTab = params[queryKeys.activeDrawerTab] ?? '';
  const isOldDrawerDisplayedRef = useRef(false);

  // Opened from an earnings entry point we only have the earnings id, so
  // resolve the matching KG insight id (by active tab) on the fly.
  const shouldResolveFromEarnings =
    !insightId && !!earningsId && !isOldDrawerDisplayedRef.current;
  const {
    data: earningsInsights,
    isLoading: isResolvingInsightId,
    isError: isEarningsInsightsError,
  } = useKgInsightFilter(
    { subject_refs: [{ type: 'earnings', id: earningsId }] },
    shouldResolveFromEarnings
  );

  const kgInsightId =
    insightId ||
    (earningsInsights?.find(insight => insight.category === activeTab)?.id ??
      '');

  // !IMPORTANT
  // Persist the resolved id to the URL so the rest of the drawer (download,
  // "read full catalyst" navigation, deep links) keeps working.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  useEffect(() => {
    if (!insightId && kgInsightId) {
      setQueryParams({ [queryKeys.insightId]: kgInsightId }, { replace: true });
    }
  }, [insightId, kgInsightId]);

  const {
    data: kgInsight,
    isLoading,
    error: kgInsightError,
  } = useKgInsight(kgInsightId);

  if (isResolvingInsightId || isLoading) {
    return <KgInsightSkeleton />;
  }

  if (kgInsightError || isEarningsInsightsError) {
    return <ErrorMessageWrapper variant="error" />;
  }

  // No matching KG insight for this earnings entry — fall back to the
  // classic earnings insight drawer content.
  if (!kgInsight || isOldDrawerDisplayedRef.current) {
    isOldDrawerDisplayedRef.current = true;
    return (
      <EarningsInsightWidget
        expandCitations={expandCitations}
        entity={entity}
      />
    );
  }

  isOldDrawerDisplayedRef.current = false;
  return (
    <EarningsKgInsightWidget
      entity={entity}
      navigateToKGInsight={navigateToKGInsight}
      kgInsight={kgInsight}
    />
  );
};
